import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useNotification } from "@/shared/hooks/useNotification";
import {
  usePrintPaymentReceipt,
  usePaymentsByOrderId,
  useProcessRefund,
} from "@/features/payments/hooks/usePayments";
import type { Payment } from "@/features/payments/services/paymentsApi";
import { PAYMENT_APPROVAL_STATUS } from "@/features/payments/services/paymentsApi";
import { openReceiptPdfForPrint } from "@/features/payments/services/paymentReceiptPdf";
import { ReprintPaymentReceiptDialog } from "@/features/payments/components/ReprintPaymentReceiptDialog";
import type { Order } from "../../services/ordersApi";

interface ViewOrderPaymentsDialogProps {
  order: Order;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PAYMENT_METHODS = [
  "Cash",
  "Bank Transfer",
  "Mobile Money",
  "Check",
  "Credit Card",
];

export const ViewOrderPaymentsDialog = ({
  order,
  open,
  onOpenChange,
}: ViewOrderPaymentsDialogProps) => {
  const notification = useNotification();
  const orderPaymentsQuery = usePaymentsByOrderId(order.id);
  const processRefund = useProcessRefund();
  const printPaymentReceipt = usePrintPaymentReceipt();

  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundPayment, setRefundPayment] = useState<Payment | null>(null);
  const [reprintDialogOpen, setReprintDialogOpen] = useState(false);
  const [reprintPayment, setReprintPayment] = useState<Payment | null>(null);
  const [refundForm, setRefundForm] = useState({
    amount: "",
    reason: "",
    paymentMethod: "",
    paymentReference: "",
    notes: "",
  });

  const rows = orderPaymentsQuery.data ?? [];

  const resetRefundForm = () => {
    setRefundPayment(null);
    setRefundForm({
      amount: "",
      reason: "",
      paymentMethod: "",
      paymentReference: "",
      notes: "",
    });
  };

  const openRefundDialog = (payment: Payment) => {
    setRefundPayment(payment);
    setRefundForm({
      amount: String(payment.amount),
      reason: "",
      paymentMethod: payment.paymentMethod || "",
      paymentReference: payment.paymentReference || "",
      notes: "",
    });
    setRefundDialogOpen(true);
  };

  const openReprintDialog = (payment: Payment) => {
    setReprintPayment(payment);
    setReprintDialogOpen(true);
  };

  const handleReprintReceipt = (payload: {
    reprintReason: string;
    format: import("@/features/payments/services/paymentsApi").ReceiptFormat;
  }) => {
    if (!reprintPayment) {
      return;
    }

    printPaymentReceipt.mutate(
      {
        paymentId: reprintPayment.id,
        payload: {
          isReprint: true,
          reprintReason: payload.reprintReason,
          format: payload.format,
        },
      },
      {
        onSuccess: (receiptBlob) => {
          const opened = openReceiptPdfForPrint(receiptBlob);
          if (!opened) {
            notification.info("Enable pop-ups to open receipt for printing.");
            return;
          }

          notification.success(`Receipt reprint opened for ${reprintPayment.paymentNumber}`);
          setReprintDialogOpen(false);
          setReprintPayment(null);
        },
        onError: () => {
          notification.error("Failed to reprint receipt");
        },
      }
    );
  };

  const handleRefund = () => {
    if (!refundPayment) {
      return;
    }

    if (!refundForm.amount || Number(refundForm.amount) <= 0) {
      notification.error("Refund amount must be greater than 0");
      return;
    }

    if (!refundForm.reason.trim() || !refundForm.paymentMethod.trim()) {
      notification.error("Refund reason and payment method are required");
      return;
    }

    processRefund.mutate(
      {
        paymentId: refundPayment.id,
        amount: Number(refundForm.amount),
        reason: refundForm.reason.trim(),
        paymentMethod: refundForm.paymentMethod,
        paymentReference: refundForm.paymentReference.trim() || undefined,
        notes: refundForm.notes.trim() || undefined,
      },
      {
        onSuccess: (payment) => {
          notification.success(`Refund created for ${payment.paymentNumber}`);
          setRefundDialogOpen(false);
          resetRefundForm();
        },
        onError: () => {
          notification.error("Failed to process refund");
        },
      }
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>Order Payments</DialogTitle>
            <DialogDescription>
              {order.customerName} - {order.id}
            </DialogDescription>
          </DialogHeader>

          {orderPaymentsQuery.isLoading ? (
            <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
              Loading order payments...
            </div>
          ) : orderPaymentsQuery.isError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-600">
              Failed to load order payments.
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-lg border p-8 text-center text-sm text-muted-foreground">
              No payments recorded for this order yet.
            </div>
          ) : (
            <div className="rounded-lg border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Payment #</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Refund</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell className="font-mono text-xs">{payment.paymentNumber}</TableCell>
                      <TableCell>{payment.amount.toLocaleString()}</TableCell>
                      <TableCell>{payment.paymentMethod}</TableCell>
                      <TableCell>{new Date(payment.paymentDate).toLocaleDateString()}</TableCell>
                      <TableCell>
                        {payment.approvalStatus === PAYMENT_APPROVAL_STATUS.Approved ? (
                          <Badge variant="default">Approved</Badge>
                        ) : payment.approvalStatus === PAYMENT_APPROVAL_STATUS.Rejected ? (
                          <Badge variant="destructive" title={payment.rejectionReason ?? undefined}>Rejected</Badge>
                        ) : (
                          <Badge variant="outline">Pending Approval</Badge>
                        )}
                      </TableCell>
                      <TableCell>{payment.isRefunded ? "Refunded" : "Not Refunded"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openReprintDialog(payment)}
                            // Block reprint for walk-in split tender until every leg is approved.
                            disabled={
                              printPaymentReceipt.isPending ||
                              (payment.orderIsWalkIn === true && payment.orderFullyPaid !== true)
                            }
                          >
                            Reprint Receipt
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openRefundDialog(payment)}
                            disabled={
                              payment.isRefunded ||
                              processRefund.isPending ||
                              printPaymentReceipt.isPending
                            }
                          >
                            Refund
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ReprintPaymentReceiptDialog
        open={reprintDialogOpen}
        onOpenChange={(nextOpen) => {
          setReprintDialogOpen(nextOpen);
          if (!nextOpen) {
            setReprintPayment(null);
          }
        }}
        payment={reprintPayment}
        isSubmitting={printPaymentReceipt.isPending}
        onSubmit={handleReprintReceipt}
      />

      <Dialog
        open={refundDialogOpen}
        onOpenChange={(nextOpen) => {
          setRefundDialogOpen(nextOpen);
          if (!nextOpen) {
            resetRefundForm();
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Process Refund</DialogTitle>
            <DialogDescription>
              {refundPayment
                ? `Create refund for ${refundPayment.paymentNumber}`
                : "Create refund transaction"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Amount</p>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={refundForm.amount}
                onChange={(event) =>
                  setRefundForm((prev) => ({ ...prev, amount: event.target.value }))
                }
                disabled={processRefund.isPending}
              />
            </div>

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Reason</p>
              <Textarea
                value={refundForm.reason}
                onChange={(event) =>
                  setRefundForm((prev) => ({ ...prev, reason: event.target.value }))
                }
                placeholder="Reason for refund"
                disabled={processRefund.isPending}
              />
            </div>

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Payment Method</p>
              <Select
                value={refundForm.paymentMethod}
                onValueChange={(value) =>
                  setRefundForm((prev) => ({ ...prev, paymentMethod: value }))
                }
                disabled={processRefund.isPending}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select method" />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((method) => (
                    <SelectItem key={method} value={method}>
                      {method}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Payment Reference</p>
              <Input
                value={refundForm.paymentReference}
                onChange={(event) =>
                  setRefundForm((prev) => ({ ...prev, paymentReference: event.target.value }))
                }
                placeholder="Reference"
                disabled={processRefund.isPending}
              />
            </div>

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Notes</p>
              <Textarea
                value={refundForm.notes}
                onChange={(event) =>
                  setRefundForm((prev) => ({ ...prev, notes: event.target.value }))
                }
                placeholder="Additional notes"
                disabled={processRefund.isPending}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setRefundDialogOpen(false)}
              disabled={processRefund.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleRefund} disabled={processRefund.isPending}>
              {processRefund.isPending ? "Processing..." : "Process Refund"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
